import { GoogleGenAI } from "@google/genai";
import { processarBoletoExtraido } from "../utils/boletoUtils";

const DEFAULT_GEMINI_KEY = "AQ.Ab8RN6JHnAf_I-kSowzILQUxGBAkJZ2DV-k9cWA9OaLbqK407Q";
const MODELS_CASCADE = [
  "gemini-flash-lite-latest",
  "gemini-3.1-flash-lite",
  "gemini-2.5-flash",
  "gemini-3.5-flash-lite",
  "gemini-3.5-flash",
  "gemini-flash-latest"
];

/**
 * Normaliza e limpa respostas JSON retornadas pela IA
 */
function parseAiJsonResponse(rawText: string): any {
  if (!rawText) {
    throw new Error("Resposta vazia da IA");
  }

  let cleaned = rawText.trim();
  // Remove blocos de código Markdown se houver ```json ... ```
  if (cleaned.startsWith("```")) {
    cleaned = cleaned.replace(/^```(?:json)?\s*/i, "").replace(/\s*```$/, "").trim();
  }

  try {
    return JSON.parse(cleaned);
  } catch (err) {
    // Tenta encontrar o primeiro { ou [ e o último } ou ]
    const startObj = cleaned.indexOf("{");
    const endObj = cleaned.lastIndexOf("}");
    if (startObj !== -1 && endObj !== -1 && endObj > startObj) {
      try {
        return JSON.parse(cleaned.substring(startObj, endObj + 1));
      } catch (innerErr) {
        console.error("Falha ao fazer parse de JSON extraído:", cleaned);
      }
    }
    console.error("Erro ao fazer parse do JSON bruto retornado pela IA:", rawText);
    throw new Error("A IA não retornou um JSON válido");
  }
}

export async function extractDataWithAI(
  base64Data: string,
  mimeType: string,
  prompt: string,
  schema: any,
  modelIndex = 0
): Promise<any> {
  const currentModel = MODELS_CASCADE[modelIndex] || MODELS_CASCADE[0];

  try {
    console.log(`[IA Service] Extraindo com modelo ultrarrápido ${currentModel}...`);
    
    // Remover o prefixo data:mime/type;base64, se existir
    let cleanBase64 = base64Data;
    if (base64Data.includes("base64,")) {
      cleanBase64 = base64Data.split("base64,")[1];
    }

    // Identificar e padronizar o mimeType
    let normalizedMimeType = mimeType || "image/jpeg";
    if (normalizedMimeType.includes("pdf")) {
      normalizedMimeType = "application/pdf";
    } else if (!normalizedMimeType.startsWith("image/") && !normalizedMimeType.startsWith("application/")) {
      normalizedMimeType = "image/jpeg";
    }

    const apiKey = process.env.GEMINI_API_KEY || DEFAULT_GEMINI_KEY;
    const ai = new GoogleGenAI({ apiKey });
    
    const config: any = {
      temperature: 0.0, // Temperatura ZERO: precisão matemática e factual, zero alucinações
      systemInstruction: "Você é um assistente de OCR e contabilidade estritamente factual. Extraia APENAS o que está visível e comprovável no documento. NUNCA invente números, datas ou nomes. Se um dado não existir no documento, retorne string vazia \"\" ou null. Para códigos de barras e linhas digitáveis, extraia apenas os dígitos reais legíveis no documento."
    };

    if (schema) {
      config.responseMimeType = "application/json";
      config.responseSchema = schema;
    }

    const response = await ai.models.generateContent({
      model: currentModel,
      contents: {
        parts: [
          {
            inlineData: {
              mimeType: normalizedMimeType,
              data: cleanBase64,
            },
          },
          {
            text: prompt,
          },
        ],
      },
      config,
    });

    const text = response.text;
    const result = parseAiJsonResponse(text || "");

    // Pós-processamento de sanitização e harmonização de boletos
    if (result && typeof result === "object") {
      if (result.linha_digitavel || result.codigo_barras) {
        const { codigo_barras, linha_digitavel } = processarBoletoExtraido(
          result.codigo_barras,
          result.linha_digitavel
        );
        if (codigo_barras) result.codigo_barras = codigo_barras;
        if (linha_digitavel) result.linha_digitavel = linha_digitavel;
      }
      if (result.amount !== undefined && result.amount !== null) {
        result.amount = typeof result.amount === "number" ? result.amount : parseFloat(String(result.amount).replace(/[^0-9.,]/g, "").replace(",", "."));
      }
      if (result.valor_bruto !== undefined && result.valor_bruto !== null) {
        result.valor_bruto = typeof result.valor_bruto === "number" ? result.valor_bruto : parseFloat(String(result.valor_bruto).replace(/[^0-9.,]/g, "").replace(",", "."));
      }
    }

    console.log(`[IA Service] Extração concluída em alta velocidade via ${currentModel}`);
    return result;
  } catch (error: any) {
    console.warn(`[IA Service] Falha com modelo ${currentModel}:`, error?.message || error);
    
    // Se for erro temporário de sobrecarga (503/429), faz uma pausa rápida de 200ms
    const isTemp = String(error?.message || error).includes("503") || String(error?.message || error).includes("429");
    if (isTemp) {
      await new Promise(r => setTimeout(r, 200));
    }

    // Tenta o próximo modelo do cascade
    if (modelIndex + 1 < MODELS_CASCADE.length) {
      console.log(`[IA Service] Alternando para o próximo modelo (${MODELS_CASCADE[modelIndex + 1]})...`);
      return extractDataWithAI(base64Data, mimeType, prompt, schema, modelIndex + 1);
    }
    
    throw new Error(`Falha ao ler documento: ${error instanceof Error ? error.message : String(error)}`);
  }
}
